/**
 * ImagePackModal (WP-75, lms-plan 18.34): the "Import image pack" dialog on a
 * course page in Manage -> Catalog. A pack is a set of reviewed illustrations
 * whose placement ships with the code; the admin picks the pack (when the
 * course has more than one), chooses the image files or the zip Canva
 * downloaded them as, and applies it. The server stores the images, places one
 * image slide per item after its named slide on the course's draft (made from
 * the current version when none is open) and answers what it placed; the
 * modal then offers "Open the draft", where the admin reads it and clicks
 * Publish. Nothing here publishes (G8), and no model is called.
 *
 * Props: { isOpen, onClose, courseId, packs, onApplied(result) }. The result
 * view stays until the admin opens the draft or closes the dialog.
 */
import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ImageDown, Loader2 } from 'lucide-react';
import Modal from '../../../../ui/Modal';
import { fieldCls, outlineBtnCls, primaryBtnCls } from '../../../../ui/styles';
import { apiErrorCode, apiErrorMessage } from '../../../api/client';
import { applyImagePack, IMAGE_PACK_ACCEPT, packFilesProblem, placementLabel } from '../../../api/imagePacks';
import type { ImagePackResult, ImagePackSummary } from '../../../api/imagePacks';
import { noticeCls } from '../../../components/player/styles';
import { hintCls, tableWrapCls, tdCls, thCls } from './styles';

interface ImagePackModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: string;
  packs: ImagePackSummary[];
  /** Called once per successful apply with the server's answer; the caller reloads its versions. */
  onApplied: (result: ImagePackResult) => void;
  /** Opens the draft the result names (the caller navigates). */
  onOpenDraft: (versionId: string) => void;
}

function warningText(w: ImagePackResult['warnings'][number]): string {
  if (w.kind === 'after_slide_missing') {
    return `Image ${w.ordinal}: slide ${w.after_slide_id ?? ''} is no longer in section ${w.section_id ?? ''}, so the image was placed at the end of that section.`;
  }
  return `Image ${w.ordinal ?? ''}: ${w.kind.replace(/_/g, ' ')}.`;
}

export function ImagePackModal({ isOpen, onClose, courseId, packs, onApplied, onOpenDraft }: ImagePackModalProps) {
  const [packId, setPackId] = useState(packs[0]?.id ?? '');
  const [files, setFiles] = useState<File[]>([]);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState<number[]>([]);
  const [result, setResult] = useState<ImagePackResult | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setPackId((current) => (packs.some((p) => p.id === current) ? current : packs[0]?.id ?? ''));
    setFiles([]);
    setError(null);
    setMissing([]);
    setResult(null);
  }, [isOpen, packs]);

  const pack = useMemo(() => packs.find((p) => p.id === packId) ?? null, [packs, packId]);
  const problem = files.length > 0 ? packFilesProblem(files, pack) : null;

  const apply = async () => {
    if (!pack || applying) return;
    const deviceProblem = packFilesProblem(files, pack);
    if (deviceProblem) {
      setError(deviceProblem);
      return;
    }
    setApplying(true);
    setError(null);
    setMissing([]);
    try {
      const answer = await applyImagePack(courseId, pack.id, files);
      setResult(answer);
      onApplied(answer);
    } catch (err) {
      const code = apiErrorCode(err);
      const body = (err as { response?: { data?: { missing?: unknown } } })?.response?.data;
      if (code === 'LMS_IMAGE_PACK_INCOMPLETE' && body && Array.isArray(body.missing)) setMissing(body.missing as number[]);
      setError(apiErrorMessage(err, 'The image pack could not be applied.'));
    } finally {
      setApplying(false);
    }
  };

  const chosenLabel = files.length === 0 ? 'No files chosen' : files.length === 1 ? files[0].name : `${files.length} files`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Import image pack" maxWidth="max-w-3xl">
      <div className="space-y-4 p-4" data-testid="lms-image-pack-modal">
        {result ? (
          <div className="space-y-3" data-testid="lms-image-pack-result">
            <div className={noticeCls('green')} role="status">
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                {result.placed.length === 0
                  ? `Nothing new to place: all ${result.skipped.length} images of this pack were already on the draft.`
                  : `${result.placed.length} image${result.placed.length === 1 ? '' : 's'} placed on draft version ${result.version.version.version_no}${result.created_draft ? ' (new)' : ''}.`}
              </span>
            </div>
            {result.skipped.length > 0 && result.placed.length > 0 ? (
              <p className={hintCls}>{`${result.skipped.length} already on the draft and left as they were.`}</p>
            ) : null}
            {result.warnings.length > 0 ? (
              <ul className={`${noticeCls('amber')} list-disc space-y-1 pl-5`} data-testid="lms-image-pack-warnings">
                {result.warnings.map((w, i) => (
                  <li key={i}>{warningText(w)}</li>
                ))}
              </ul>
            ) : null}
            {result.version.lint && !result.version.lint.ok ? (
              <p className={noticeCls('amber')}>The draft has lint problems to fix before it can be published; the editor lists them.</p>
            ) : null}
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Open the draft to see each image on its slide, then publish it. Nothing is published until you do.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className={outlineBtnCls()} onClick={onClose}>
                Close
              </button>
              <button type="button" className={primaryBtnCls} onClick={() => onOpenDraft(result.version.version.id)} data-testid="lms-image-pack-open-draft">
                {`Open draft v${result.version.version.version_no}`}
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              An image pack places reviewed illustrations on this course for you: each image goes on its own slide, in the section and after
              the slide the pack names, with its heading, alt text and caption. Upload the pack&apos;s images (or the zip they came in), apply,
              then open the draft and publish it.
            </p>
            {packs.length > 1 ? (
              <label className="block space-y-1">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Pack</span>
                <select className={fieldCls} value={packId} onChange={(e) => setPackId(e.target.value)} data-testid="lms-image-pack-select">
                  {packs.map((p) => (
                    <option key={p.id} value={p.id}>
                      {`${p.title} (${p.item_count} images)`}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {pack ? (
              <div className="space-y-2">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100" data-testid="lms-image-pack-title">
                    {`${pack.title} (${pack.item_count} images)`}
                  </h3>
                  {pack.description ? <p className={hintCls}>{pack.description}</p> : null}
                </div>
                <div className={`${tableWrapCls} max-h-64 overflow-y-auto`}>
                  <table className="min-w-full text-sm" data-testid="lms-image-pack-items">
                    <thead>
                      <tr>
                        <th className={thCls}>#</th>
                        <th className={thCls}>File</th>
                        <th className={thCls}>Goes</th>
                        <th className={thCls}>Slide heading</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pack.items.map((item) => (
                        <tr key={item.ordinal} className={missing.includes(item.ordinal) ? 'bg-red-50 dark:bg-red-950/30' : ''}>
                          <td className={tdCls}>{item.ordinal}</td>
                          <td className={`${tdCls} font-mono text-xs`}>{item.file}</td>
                          <td className={tdCls}>{placementLabel(item)}</td>
                          <td className={tdCls}>{item.heading}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className={hintCls}>
                  Name the files as listed, or upload exactly one image per row in order (1, 2, 3 ... the way Canva numbers downloaded pages).
                  PNG, JPEG or WebP, up to 25 MB each, or one zip of them.
                </p>
              </div>
            ) : (
              <p className={hintCls}>No image pack is shipped for this course.</p>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <label className={`${outlineBtnCls()} cursor-pointer`}>
                <ImageDown className="h-4 w-4" aria-hidden="true" />
                Choose files
                <input
                  type="file"
                  className="sr-only"
                  multiple
                  accept={IMAGE_PACK_ACCEPT}
                  onChange={(e) => {
                    setFiles(Array.from(e.target.files ?? []));
                    setError(null);
                    setMissing([]);
                  }}
                  data-testid="lms-image-pack-files"
                />
              </label>
              <span className="text-sm text-gray-700 dark:text-gray-300" data-testid="lms-image-pack-chosen">
                {chosenLabel}
              </span>
            </div>
            {problem && !error ? (
              <p className={noticeCls('amber')} role="alert" data-testid="lms-image-pack-problem">
                {problem}
              </p>
            ) : null}
            {error ? (
              <p className={noticeCls('red')} role="alert" data-testid="lms-image-pack-error">
                {error}
              </p>
            ) : null}
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className={outlineBtnCls()} onClick={onClose} disabled={applying}>
                Cancel
              </button>
              <button
                type="button"
                className={primaryBtnCls}
                onClick={apply}
                disabled={!pack || files.length === 0 || Boolean(problem) || applying}
                data-testid="lms-image-pack-apply"
              >
                {applying ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <ImageDown className="h-4 w-4" aria-hidden="true" />}
                {applying ? 'Placing the images...' : 'Apply to the draft'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default ImagePackModal;
